import { FC } from "react";

import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import Image from "apps/website/components/base/Image/Image";
import Icon from "apps/website/components/base/Icon/Icon";
import Tag from "apps/website/components/base/Tag/Tag";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { pluralise } from "apps/website/utils/misc/strings";
import { ISummaryDetailFreshSprinkles } from "@forms/schema";

import SummaryDetailedImageDescription from "./SummaryDetailedImageDescription";

interface ISummaryDetailedHeader {
  discountPercentage?: number;
  trialCostPerDayWithoutDiscount?: string;
  trialCostPerDayWithDiscount?: string;
  hasChickenSprinkles: boolean;
  itemsLength: number;
  chickenSprinklesData?: ISummaryDetailFreshSprinkles
}

const SummaryDetailedHeader: FC<ISummaryDetailedHeader> = ({
  discountPercentage,
  trialCostPerDayWithoutDiscount,
  trialCostPerDayWithDiscount,
  hasChickenSprinkles,
  itemsLength,
  chickenSprinklesData,
}) => (
  <>
    <div
      className={`text-left px-2 py-2 mb-2 lg:mb-4 ${themeRootClassMap.dark}`}
      data-theme="dark"
    >
      <div className="flex items-center justify-between">
        <TextSubtitle tag="span" size={2} color="default">
            Trial box { discountPercentage ? <>({ discountPercentage }% off)</> : null }
        </TextSubtitle>
        <div
          data-theme="light-grey"
          className={`rounded-full px-2 py-1 ${themeRootClassMap["light-grey"]}`}
        >
          <div className="flex items-center">
            <TextBody
              tag="s"
              size={3}
              color="default"
              className="mr-1 leading-none"
            >
              { trialCostPerDayWithoutDiscount }
            </TextBody>
            <TextSubtitle
              tag="span"
              size={2}
              color="default"
              className="leading-none"
            >
              { trialCostPerDayWithDiscount } / day
            </TextSubtitle>
          </div>
        </div>
      </div>
    </div>

    <div className="mb-2 lg:mb-4">
      <Grid className="relative">
        <>
          <Column spans={hasChickenSprinkles ? 6 : 12} data-theme="light" className={`rounded-md ${themeRootClassMap.light}`}>
            <Grid gap="none">
              <>
                <Column spans={!hasChickenSprinkles ? 6 : 12}>
                  <Image image={{ src: "/images/dev/components/DetailedSummary/food.webp", width: 491, height: 255 }} alt="14 days of food" />
                </Column>
                { !hasChickenSprinkles ? (
                  <Column spans={6} justify="center" align="center">
                    <SummaryDetailedImageDescription
                      title={`14 days of food${pluralise("", " per cat", itemsLength)}. Perfectly portioned.`}
                      body="No need to feed anything else!"
                      align="default"
                    />
                  </Column>
                ) : null }
              </>
            </Grid>
          </Column>
          { hasChickenSprinkles ? (
            <>
              <Column spans={6} data-theme="light" className={`rounded-md ${themeRootClassMap.light}`}>
                <Image image={{ src: "/images/dev/components/DetailedSummary/nibbles.webp", width: 491, height: 255 }} alt="Nibbles" />
              </Column>
              <div
                className="w-[36px] h-[36px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-light-grey"
              >
                <Icon icon="plusHeavy" size="fit" />
              </div>
            </>
          ) : null }
        </>
      </Grid>
    </div>
    { hasChickenSprinkles ? (
      <div className="mb-2 lg:mb-4">
        <Grid>
          <Column spans={6}>
            <SummaryDetailedImageDescription
              title={`14 days of food${pluralise("", " per cat", itemsLength)}`}
              body="Perfectly portioned. No need to feed anything else!"
            />
          </Column>
          <Column spans={6}>
            <SummaryDetailedImageDescription
              title={`Chicken Sprinkles${pluralise("", ` (x${chickenSprinklesData?.quantity})`, chickenSprinklesData?.quantity ?? 1)}`}
            />
            <Spacer size="sm" />
            <div className="flex space-x-2 justify-center items-center" data-theme="light-grey">
              <TextBody size={2} tag="s" color="secondary">£{ chickenSprinklesData?.savings.toFixed(2) }</TextBody>
              <Tag theme="pink">Free gift</Tag>
            </div>
          </Column>
        </Grid>
      </div>
    ) : null }
  </>
);

export default SummaryDetailedHeader;
